@let form = assistantForm();
@let toolForm = activeToolForm();
@let capabilityForm = activeCapabilityForm();

<glxy-page [pagePadding]="!!(toolForm || capabilityForm || showCombinedPrompt())">
  <glxy-page-toolbar>
    <glxy-page-nav>
      @if (toolForm || capabilityForm || showCombinedPrompt()) {
        <ng-container *ngTemplateOutlet="fauxNavButton"></ng-container>
      } @else {
        <glxy-page-nav-button
          [useHistory]="true"
          previousPageUrl="../../"
          [useRelativePath]="true"
        ></glxy-page-nav-button>
      }
    </glxy-page-nav>
    <glxy-page-title>
      {{ 'AI_ASSISTANT.SETTINGS.TITLE' | translate }}
      @if (capabilityForm) {
        <mat-icon class="breadcrumb-separator" aria-hidden="true">chevron_right</mat-icon>
        {{ 'AI_ASSISTANT.SETTINGS.CAPABILITY' | translate }}
        @if (toolForm) {
          <mat-icon class="breadcrumb-separator" aria-hidden="true">chevron_right</mat-icon>
          {{ 'AI_ASSISTANT.SETTINGS.TOOL' | translate }}
        }
      }
      @if (showCombinedPrompt()) {
        <mat-icon class="breadcrumb-separator" aria-hidden="true">chevron_right</mat-icon>
        {{ 'AI_ASSISTANT.SETTINGS.PROFILE_CAPABILITY.COMBINED_PROMPT.TITLE' | translate }}
      }
    </glxy-page-title>
    @if (shouldShowChatButton()) {
      <glxy-page-actions>
        <button mat-flat-button color="primary" (click)="onChatButtonClick()">
          {{ 'AI_ASSISTANT.WORKFORCE.CHAT_BUTTON' | translate }}
        </button>
      </glxy-page-actions>
    }
    @if (showDeleteButton()) {
      <glxy-page-actions>
        <button mat-stroked-button (click)="openDeleteAssistantConfirmationModal()">
          {{ 'AI_ASSISTANT.SHARED.DELETE' | translate }}
        </button>
      </glxy-page-actions>
    }
    @if (capabilityForm && !toolForm) {
      <glxy-page-actions>
        <button
          mat-stroked-button
          (click)="copyCapability()"
          class="copy-button"
          [attr.aria-label]="'AI_ASSISTANT.SETTINGS.COPY_CAPABILITY' | translate"
          [glxyTooltip]="'AI_ASSISTANT.SETTINGS.COPY_CAPABILITY_TOOLTIP' | translate"
        >
          <mat-icon>content_copy</mat-icon>
          <span class="copy-text">{{ 'AI_ASSISTANT.SETTINGS.COPY_CAPABILITY' | translate }}</span>
        </button>
      </glxy-page-actions>
    }
    @if (showCombinedPrompt()) {
      <glxy-page-actions>
        <button
          mat-stroked-button
          (click)="copyPromptToClipboard()"
          [glxyTooltip]="'AI_ASSISTANT.SETTINGS.PROFILE_CAPABILITY.COMBINED_PROMPT.TOOLTIP' | translate"
        >
          <mat-icon>content_copy</mat-icon>
          {{ 'AI_ASSISTANT.SETTINGS.COPY_CAPABILITY' | translate }}
        </button>
      </glxy-page-actions>
    }
  </glxy-page-toolbar>
  @if (assistantNotFound()) {
    <glxy-page-wrapper class="assistant-not-found-wrapper">
      <mat-card class="assistant-not-found-card">
        <glxy-empty-state [size]="'small'">
          <glxy-empty-state-hero>
            <mat-icon>error</mat-icon>
          </glxy-empty-state-hero>
          <glxy-empty-state-title>{{ 'AI_ASSISTANT.SETTINGS.ASSISTANT_NOT_FOUND' | translate }}</glxy-empty-state-title>
          <p>{{ 'AI_ASSISTANT.SETTINGS.ERROR_FETCHING_ASSISTANT' | translate }}</p>
        </glxy-empty-state>
      </mat-card>
    </glxy-page-wrapper>
  } @else if (loading()) {
    <glxy-loading-spinner></glxy-loading-spinner>
  } @else if (form) {
    @if (toolForm) {
      <glxy-page-wrapper>
        <ai-tool-form [form]="toolForm.form" class="tool-form" />
        <ai-sticky-footer
          (submitClick)="handleToolFormSubmit()"
          [submitOverride]="'AI_ASSISTANT.FUNCTIONS.EDIT_FUNCTION.DONE' | translate"
          [hideCancel]="!showToolFormCancel()"
          (cancelClick)="closeToolForm()"
          [showUnsavedChangesIndicator]="toolForm.form.dirty"
        />
      </glxy-page-wrapper>
    } @else if (capabilityForm) {
      <glxy-page-wrapper>
        @if (isEditingGlobalOrManagedCapability()) {
          <glxy-alert type="info">
            <strong>{{ 'AI_ASSISTANT.SETTINGS.LOCAL_OVERRIDE_TITLE' | translate }}</strong>
            {{ 'AI_ASSISTANT.SETTINGS.LOCAL_OVERRIDE_EXPLANATION' | translate }}
          </glxy-alert>
        }
        <ai-capability-form
          [capabilityForm]="capabilityForm"
          [assistantType]="aiAssistant()?.assistant?.type"
          class="capability-form"
          (toolFormDisplay)="
            handleToolFormDisplay(
              $event !== undefined ? { form: $event, parentCapabilityForm: capabilityForm } : undefined
            )
          "
          [allowRemovingAnyTools]="true"
        />
        <ai-sticky-footer
          (submitClick)="handleCapabilityFormSubmit()"
          [submitOverride]="'AI_ASSISTANT.FUNCTIONS.EDIT_FUNCTION.DONE' | translate"
          [hideCancel]="!showCapabilityFormCancel()"
          (cancelClick)="closeCapabilityForm()"
          [showUnsavedChangesIndicator]="capabilityForm.dirty"
        />
      </glxy-page-wrapper>
    } @else if (showCombinedPrompt()) {
      <glxy-page-wrapper>
        <div class="combined-prompt-viewer">
          <h2 class="prompt-title">{{ headerText$ | async }}</h2>
          <div class="prompt-content">
            <pre [innerHTML]="combinedPrompt$ | async"></pre>
          </div>
        </div>
      </glxy-page-wrapper>
    }

    <form
      class="config-page-wrapper"
      [formGroup]="form"
      [ngClass]="{ hidden: toolForm || capabilityForm || showCombinedPrompt() }"
    >
      <div class="two-column-layout">
        <div class="left-column">
          <ai-image-upload
            (imageUrlChanged)="onImageChanged($event)"
            [imageUrl]="form.controls.avatarUrl.value"
            [fallbackImageUrl]="aiAssistant()?.decoration?.avatarIconUrl"
            [fallbackSvg]="aiAssistant()?.decoration?.defaultAvatarIcon ?? defaultAvatarIcon"
            class="avatar"
          />

          <div>
            <!-- Name -->
            <glxy-form-field>
              <glxy-label>
                {{ 'AI_ASSISTANT.SETTINGS.ASSISTANT_NAME' | translate }}
              </glxy-label>
              <input matInput id="assistant-name" formControlName="name" [placeholder]="defaultAssistantName()" />
              @if (aiAssistant()?.subtitleKey && !isCustomAssistant()) {
                <glxy-hint>{{ aiAssistant()?.subtitleKey || '' | translate }}</glxy-hint>
              }
            </glxy-form-field>

            @if (isCustomAssistant()) {
              <!-- Role -->
              <glxy-form-field>
                <glxy-label>
                  {{ 'AI_ASSISTANT.SETTINGS.ROLE' | translate }}
                </glxy-label>
                <input matInput id="assistant-role" formControlName="role" />
              </glxy-form-field>

              <!-- Description -->
              <glxy-form-field>
                <glxy-label>
                  {{ 'AI_ASSISTANT.SETTINGS.DESCRIPTION' | translate }}
                </glxy-label>
                <textarea
                  matInput
                  id="assistant-description"
                  formControlName="description"
                  cdkTextareaAutosize
                  cdkAutosizeMinRows="3"
                  cdkAutosizeMaxRows="10"
                ></textarea>
              </glxy-form-field>
            }
          </div>

          @if (isSalesCoach()) {
            <button mat-stroked-button class="view-recorded-btn" (click)="viewRecordedMeetings()">
              {{ 'AI_ASSISTANT.SETTINGS.VIEW_RECORDED_MEETINGS' | translate }}
            </button>
          }

          <!-- Voice Usage Information -->
          @if (assistantSupportsVoice()) {
            <glxy-alert type="info">
              <div>
                <strong>Important</strong>
                &mdash; {{ 'AI_ASSISTANT.SETTINGS.VOICE_BETA_BANNER_MESSAGE' | translate }}
              </div>
              <ai-assistant-voice-usage
                class="voice-usage"
                [namespace]="aiAssistant()?.assistant?.namespace"
              ></ai-assistant-voice-usage>
            </glxy-alert>
          }

          <!-- Autonomy information with popover -->
          @let autonomyRating = aiAssistant()?.autonomyRating;
          @if (autonomyRating) {
            @let autonomy = autonomyRating | autonomyLevel;

            <div class="autonomy-display">
              <span class="autonomy-label"
                >{{ 'AI_ASSISTANT.AUTONOMY_LABEL' | translate }}
                <button
                  mat-icon-button
                  (click)="toggleAutonomyPopover()"
                  cdkOverlayOrigin
                  #popoverOrigin="cdkOverlayOrigin"
                  aria-label="Autonomy info"
                  class="autonomy-info-button"
                >
                  <mat-icon class="info-icon">info_outline</mat-icon>
                </button>
                @if (showAutonomyPopover) {
                  <glxy-popover
                    [origin]="popoverOrigin"
                    [(isOpen)]="showAutonomyPopover"
                    [showBackdrop]="true"
                    [hasBackdrop]="true"
                    [hasArrow]="true"
                    [positions]="[PopoverPositions.Right, PopoverPositions.Bottom, PopoverPositions.Top]"
                    [maxWidth]="500"
                    (backdropClick)="closeAutonomyPopover()"
                    [keepOnScreen]="true"
                    [mobileFullScreen]="true"
                  >
                    <glxy-popover-title>
                      {{ 'AI_ASSISTANT.AUTONOMY_LEVELS.TITLE' | translate }}
                    </glxy-popover-title>

                    @for (level of autonomyLevels; track level.level) {
                      <div class="autonomy-popover-section">
                        <div class="section-label">
                          {{
                            'AI_ASSISTANT.AUTONOMY_LEVELS.LEVEL_TITLE_FORMAT'
                              | translate: { level: level.level, title: (level.levelName | translate) }
                          }}
                        </div>
                        <div class="section-title">
                          {{ level.levelTitle | translate }}
                        </div>
                        <div class="section-description">
                          {{ level.levelDescription | translate }}
                        </div>
                      </div>
                    }
                  </glxy-popover>
                }
              </span>
              <span class="autonomy-level">
                {{
                  'AI_ASSISTANT.AUTONOMY_LEVELS.LEVEL_TITLE_FORMAT'
                    | translate: { level: autonomy.level, title: (autonomy.levelName | translate) }
                }}
              </span>
            </div>
          }
          <button mat-stroked-button (click)="viewPromptClicked()" class="prompt-viewer-button">
            {{ 'AI_ASSISTANT.SETTINGS.PROFILE_CAPABILITY.COMBINED_PROMPT.BUTTON' | translate }}
          </button>
        </div>
        <div class="right-column">
          <div class="form-wrapper">
            <div class="form-cards">
              <!-- Alert Information Banner -->
              @let alertInformation = aiAssistant()?.alertInformation;
              @if (alertInformation) {
                <glxy-alert type="info">
                  {{
                    alertInformation
                      | translate: { assistantName: aiAssistant()?.assistant?.name || defaultAssistantName() }
                  }}
                </glxy-alert>
              }

              @if (triggerFeatureFlagEnabled()) {
                <!-- Triggers -->
                @let connectionsForm = form?.controls?.connections;
                @if (connectionsForm && aiConnections()) {
                  <ai-connections-form
                    [connectionForms]="connectionsForm"
                    [triggerFeatureFlagEnabled]="triggerFeatureFlagEnabled()"
                    [showManageButton]="aiAssistant()?.showManageTriggersButton ?? true"
                    [reviewTriggerBanner]="reviewTriggerBanner()"
                  ></ai-connections-form>
                }
              }

              <!-- Profile -->
              <ai-profile-accordion
                [assistantForm]="form"
                [assistantSupportsVoice]="assistantSupportsVoice()"
                [availableModels]="availableModels()"
                (rollback)="handleCapabilityRollback($event)"
              />

              @if (!triggerFeatureFlagEnabled()) {
                <!-- Connections -->
                @let connectionsForm = form?.controls?.connections;
                @if (connectionsForm && aiConnections()) {
                  <ai-connections-form
                    [connectionForms]="connectionsForm"
                    [triggerFeatureFlagEnabled]="triggerFeatureFlagEnabled()"
                    [showManageButton]="aiAssistant()?.showManageTriggersButton ?? true"
                    [reviewTriggerBanner]="reviewTriggerBanner()"
                  ></ai-connections-form>
                }
              }

              @let capabilitiesForm = form?.controls?.capabilities;

              @if (isSituationConfigEnabled() && form?.controls?.situationConfig && capabilitiesForm) {
                <ai-situation-configuration-form
                  [situationConfigForm]="form.controls.situationConfig"
                  [availableCapabilities]="capabilitiesForm.controls"
                />
              }

              <!-- Capabilities -->
              @if (capabilitiesForm) {
                <ai-capabilities-list
                  [capabilities]="capabilitiesForm"
                  [situationConfig]="form.controls.situationConfig"
                  (capabilityFormDisplay)="handleCapabilityFormDisplay($event)"
                  (rollback)="handleCapabilityRollback($event)"
                />
              }

              <!-- Knowledge -->
              <mat-card>
                <mat-card-header class="full-width-header">
                  <mat-card-title>
                    <div class="title-row">
                      <span>{{ 'AI_ASSISTANT.SETTINGS.KNOWLEDGE_SOURCES' | translate }}</span>
                      @if (form?.controls?.knowledge?.dirty) {
                        <span class="unsaved-changes-text">
                          {{ 'AI_ASSISTANT.SHARED.UNSAVED_CHANGES.TITLE' | translate }}
                        </span>
                      }
                    </div>
                  </mat-card-title>
                  <mat-card-subtitle
                    >{{ 'AI_ASSISTANT.SETTINGS.KNOWLEDGE_SOURCES_DESCRIPTION' | translate }}
                  </mat-card-subtitle>
                </mat-card-header>
                <mat-card-content class="no-top-margin">
                  <ai-knowledge-application-knowledge
                    [appId]="appId()"
                    (selectedSourcesChange)="updateSelectedSources($event)"
                  ></ai-knowledge-application-knowledge>
                </mat-card-content>
              </mat-card>

              @if (showAutomations()) {
                <mat-card>
                  <mat-card-header class="full-width-header">
                    <mat-card-title>
                      <div class="title-row">
                        <span>{{ 'AI_ASSISTANT.SETTINGS.AUTOMATIONS' | translate }}</span>
                      </div>
                    </mat-card-title>
                    <mat-card-subtitle
                      >{{ 'AI_ASSISTANT.SETTINGS.AUTOMATIONS_DESCRIPTION' | translate }}
                    </mat-card-subtitle>
                  </mat-card-header>
                  <mat-card-content class="no-top-margin">
                    <ai-incoming-automations [employeeId]="aiAssistant()?.assistant.id"></ai-incoming-automations>
                  </mat-card-content>
                  <mat-card-footer class="card-footer">
                    <a mat-stroked-button [routerLink]="['../../../../', 'automations']">
                      {{ 'AI_ASSISTANT.SETTINGS.MANAGE_AUTOMATIONS' | translate }}
                    </a>
                  </mat-card-footer>
                </mat-card>
              }
            </div>

            <ai-sticky-footer
              [attr.class]="toolForm || capabilityForm ? 'hidden' : ''"
              (submitClick)="submit()"
              (cancelClick)="back()"
              [disabled]="submitDisabled()"
              [showUnsavedChangesIndicator]="form.dirty"
            />
          </div>
        </div>
      </div>
    </form>
  }
</glxy-page>

<ng-template #fauxNavButton>
  <div class="faux-nav-button">
    <button mat-icon-button color="primary" (click)="handleCombinedPromptSubmit()">
      <mat-icon>arrow_back</mat-icon>
    </button>
  </div>
</ng-template>
